// src/components/PickGroupList.js
//
// Renders the inner picks UI for the currently active picks group:
// either the "picks not yet available" lock notice (when every
// scheduled game in the group still has placeholder slots), or each
// round in turn as a CollapsibleRound full of PickCards.
//
// Shared by PickSubmission (the participant flow) and AdminPickEntry
// (the admin-on-behalf-of-a-participant flow). Both screens render an
// identical tree at this level; only the surrounding chrome -- entry
// selectors, summary banner, tab strip -- differs. Keeping those
// per-page and sharing only this inner block avoids over-coupling the
// two screens via prop-drilling.
//
// Props:
//   - activeGroup     : the group object from buildGroups()
//   - isGroupLocked   : true when every scheduled game lacks both teams
//   - picks           : { [gameId]: pickRow }
//   - gameMap         : { [gameId]: gameRow }
//   - saving          : { [gameId]: boolean } -- per-game spinner flag
//   - collapsedRounds : Set of round_numbers currently collapsed
//   - onToggleCollapse(roundNumber) : flip a round's collapsed state
//   - onPick          : passed straight through to each PickCard
//   - collapsibleRule : 'always' | 'multi-round-only'. PickSubmission
//                       uses 'multi-round-only' so a single-round group
//                       can't be collapsed (its toggle isn't rendered);
//                       AdminPickEntry uses 'always'.

import React               from 'react';
import CollapsibleRound    from './CollapsibleRound';
import PickCard            from './PickCard';

function PickGroupList({
    activeGroup,
    isGroupLocked,
    isPastDeadline = false,
    distribution = {},
    picks,
    gameMap,
    saving,
    collapsedRounds,
    onToggleCollapse,
    onPick,
    collapsibleRule = 'always',
    regionDisplayNames = null,
}) {
    if (!activeGroup) return null;

    if (isGroupLocked) {
        return (
            <div className="text-center py-12 px-4">
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md mx-auto">
                    <p className="text-[var(--org-accent,#fb923c)] font-bold text-sm mb-2">Picks not yet available</p>
                    <p className="text-gray-400 text-sm">
                        Picks for this group will open once the previous round's
                        results are recorded and the next deadline arrives.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            {isPastDeadline && (
                <div className="bg-gray-900 border border-red-800 rounded-xl px-5 py-4 mb-4 flex items-center gap-3">
                    <span className="text-xl">🔒</span>
                    <div>
                        <p className="text-red-400 font-bold text-sm">Picks are locked</p>
                        <p className="text-gray-400 text-xs mt-0.5">The deadline for this round has passed. Your saved picks are shown below.</p>
                    </div>
                </div>
            )}
            {activeGroup.rounds.map(round => {
                // Render ALL games in the round (including final ones),
                // so missed picks and historical context stay visible.
                // Final games render via PickCard's readOnly mode.
                // Deadline-locked games also render readOnly so buttons
                // are disabled; isPastDeadline is passed separately so
                // PickCard can apply the correct locked-but-not-final style.
                if (round.games.length === 0) return null;

                const isCollapsed = collapsedRounds.has(round.round_number);
                const roundPicked = round.games.filter(g => picks[g.id]).length;
                const collapsible =
                    collapsibleRule === 'always'
                        ? true
                        : activeGroup.rounds.length > 1;

                return (
                    <CollapsibleRound
                        key={round.round_number}
                        round={round}
                        pickedCount={roundPicked}
                        totalCount={round.games.length}
                        isCollapsed={isCollapsed}
                        onToggle={() => onToggleCollapse(round.round_number)}
                        collapsible={collapsible}
                    >
                        <div className="space-y-3">
                            {round.games.map(game => (
                                <PickCard
                                    key={game.id}
                                    game={game}
                                    round={round}
                                    myPick={picks[game.id]}
                                    picks={picks}
                                    gameMap={gameMap}
                                    saving={saving[game.id]}
                                    onPick={onPick}
                                    readOnly={game.status === 'final'}
                                    deadlineLocked={isPastDeadline && game.status !== 'final'}
                                    distribution={distribution[game.id] || null}
                                    regionDisplayNames={regionDisplayNames}
                                />
                            ))}
                        </div>
                    </CollapsibleRound>
                );
            })}
        </>
    );
}

export default PickGroupList;